import { useFormikContext } from 'formik';
import get from 'lodash/get';
import type { ReactNode, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';

import FormErrorMessage from '@tmh-website/components/shared/FormErrorMessage';
import { Color } from '@tmh-website/shared/colors';
import { FormElementCommonProps } from '@tmh-website/ui/FormElements';
import { StyledFormElements } from '@tmh-website/ui/FormElements';

import * as Styled from './Textarea.styles';

export interface TextareaProps
  extends FormElementCommonProps,
    Partial<Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'name'>> {
  helpText?: string | ReactNode;
  smallLabel?: boolean;
  maxLength?: number;
}

const Textarea = ({
  themeColor = Color.Neutral_0_White,
  disabled,
  helpText,
  label,
  name,
  maxLength,
  required,
  placeholder,
  smallLabel,
  ...props
}: TextareaProps) => {
  const uniqueId = useId();
  const nameWithId = name + '_' + uniqueId;
  const { errors, touched, values } = useFormikContext<any>();
  const hasError = errors[name] && touched[name];
  const formikValue = get(values, name);

  return (
    <div>
      <StyledFormElements.Container $themeColor={themeColor} $hasError={hasError} $isDisabled={disabled}>
        {label && (
          <StyledFormElements.InputLabel
            $themeColor={themeColor}
            $fieldIsFilled={
              (formikValue !== '' && formikValue !== undefined && formikValue !== null) || !!placeholder || !!smallLabel
            }
            htmlFor={nameWithId}>
            {label}
            {required && '*'}
          </StyledFormElements.InputLabel>
        )}
        <Styled.ScrollLabelUnderlay $themeColor={themeColor} $isDisabled={!!disabled} />
        {!!maxLength && (
          <Styled.CharacterCounter $themeColor={themeColor} $isDisabled={!!disabled}>
            {formikValue.length} / {maxLength}
          </Styled.CharacterCounter>
        )}
        <Styled.Textarea
          as='textarea'
          name={name}
          id={nameWithId}
          maxLength={maxLength}
          placeholder={placeholder}
          $themeColor={themeColor}
          $extraPaddingBottom={!!maxLength}
          $smallLabel={!!smallLabel}
          {...props}></Styled.Textarea>
      </StyledFormElements.Container>
      {helpText && <StyledFormElements.HelpText $themeColor={themeColor}>{helpText}</StyledFormElements.HelpText>}
      {hasError && (
        <StyledFormElements.ErrorMessage>
          <FormErrorMessage name={name} />
        </StyledFormElements.ErrorMessage>
      )}
    </div>
  );
};

export default Textarea;
